import * as Sentry from "@sentry/react";

let initialized = false;

/**
 * Initialize Sentry once at app boot. No-op when `VITE_SENTRY_DSN` is unset
 * (local dev, or environments that haven't been wired up yet) so capture
 * calls elsewhere remain safe.
 */
export function initSentry(): void {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE,
    tracesSampleRate: 0,
  });
  initialized = true;
}

interface AuthEventTags {
  reason?: string;
  source?: string;
  [key: string]: string | undefined;
}

/**
 * Record an authentication event (sign-out, refresh failure, token classification)
 * to Sentry as a scoped breadcrumb + message. Falls back to console when Sentry
 * is not configured so the signal still lands somewhere.
 */
export function captureAuthEvent(message: string, tags: AuthEventTags = {}): void {
  if (initialized) {
    Sentry.withScope((scope) => {
      scope.setTag("category", "auth");
      for (const [k, v] of Object.entries(tags)) {
        if (v) scope.setTag(k, v);
      }
      Sentry.captureMessage(message, "warning");
    });
  } else if (typeof console !== "undefined") {
    console.warn(`[auth] ${message}`, tags);
  }
}

export function captureAuthException(error: unknown, tags: AuthEventTags = {}): void {
  if (initialized) {
    Sentry.withScope((scope) => {
      scope.setTag("category", "auth");
      for (const [k, v] of Object.entries(tags)) {
        if (v) scope.setTag(k, v);
      }
      Sentry.captureException(error);
    });
  } else if (typeof console !== "undefined") {
    console.error("[auth]", error, tags);
  }
}
